import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { applyActionCode } from "firebase/auth";
import Nav from "../../components/nav";
import { auth } from "../../api/firebase.class";
import assinatura from "../../assets/marca/hqlab-assinatura.png";
import {
  desfechoDoErro,
  lerLinkDeAcao,
  textoDoDesfecho,
  type Desfecho,
} from "../../api/verificacao";
import "./verificar.scss";

/**
 * /verificar — A PÁGINA DO LINK DO E-MAIL DE CONFIRMAÇÃO (ticket 0055 do mvp-tickets).
 *
 * Na letra do operador: «que a página de verificação seja uma página bonitinha na estética (...) do
 * projeto real mesmo». Antes dela, o link do e-mail passava por `/auth/action` e era REDIRECIONADO
 * para a página genérica do Firebase (hq-lab-afd38.firebaseapp.com), em inglês, sem volta para o
 * HQ-Lab. Agora a confirmação acontece aqui, no cartão do tema quadrinho.
 *
 * A decisão (o que o link pede, o que cada erro quer dizer) mora em `api/verificacao.ts`, igual nas
 * quatro plataformas; aqui só se desenha. A troca de senha continua em `/auth/action`.
 */
export default function Verificar() {
  const [params] = useSearchParams();
  const [estado, setEstado] = useState<Desfecho | "conferindo" | "incompleto">("conferindo");
  const jaTentou = useRef(false); // o código é de uso único, e o StrictMode roda o efeito duas vezes

  useEffect(() => {
    const link = lerLinkDeAcao(`?${params.toString()}`, {
      origem: window.location.origin,
      authDomain: auth.app.options.authDomain ?? "hq-lab-afd38.firebaseapp.com",
      repassar: (mode) => (mode === "resetPassword" ? "/auth/action" : null),
    });
    if (link.tipo === "incompleto") return setEstado("incompleto");
    if (link.tipo === "repassar") return void window.location.replace(link.destino);
    if (jaTentou.current) return;
    jaTentou.current = true;
    applyActionCode(auth, link.codigo)
      .then(() => setEstado("verificado"))
      .catch((e) => setEstado(desfechoDoErro(e)));
  }, [params]);

  const { titulo, texto } = textoDoDesfecho(estado, "HQ-Lab");
  const deuCerto = estado === "verificado";

  return (
    <div className="verificar-container">
      <Nav />
      <section className="verificar-cartao" aria-live="polite" data-estado={estado}>
        <img src={assinatura} alt="HQ-Lab" />
        {estado !== "conferindo" && (
          <div className={`verificar-selo${deuCerto ? "" : " ruim"}`}>
            {deuCerto ? "Tudo certo!" : "Ops!"}
          </div>
        )}
        <h1>{titulo}</h1>
        <p>{texto}</p>
        {estado === "conferindo" ? null : estado === "sem-rede" ? (
          <button type="button" className="verificar-botao" onClick={() => window.location.reload()}>
            Tentar de novo
          </button>
        ) : (
          // O HQ-Lab não deixa entrar sem e-mail confirmado (firebase.class.ts:login); por isso o
          // passo seguinte é sempre a tela de entrar, que agora vai aceitar a conta.
          <Link className="verificar-botao" to="/login">
            {deuCerto ? "Entrar e criar quadrinhos" : "Ir para a tela de entrar"}
          </Link>
        )}
      </section>
    </div>
  );
}
